import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { AlertNotification, Button, LabeledIconInput, Row, Col } from "@commonsku/styles";

import { SupplierVendorCredit } from "../../types/vendor_credit";
import { getCurrencySymbol } from "../../utils";

import useDraft from "./useDraft";
import FormPopup from "../FormPopup";
import AsyncDivisionSelect from "../AsyncDivisionSelect";
import ExemptTaxSelect from "../helpers/ExemptTaxSelect";
import useCanadianTax from "../../hooks/useCanadianTax";

const SUPPLIER_VENDOR_CREDIT_POPUP_WIDTH = "640px";
const SUPPLIER_VENDOR_CREDIT_POPUP_HEIGHT = "324px";
const VENDOR_CREDIT_NOTE_PLACEHOLDER_TEXT = "Add a note (optional)";

export default function SupplierVendorCreditPopup({
  vendorCredit,
  onClose,
  isDisabled = false,
}: {
  vendorCredit: SupplierVendorCredit;
  onClose: (saved: boolean) => void;
  isDisabled?: boolean;
}) {
  const { draft, isNew, isValid, isEditable, updater, save } = useDraft(vendorCredit);
  const disabled = isDisabled || !isEditable;
  const { isCanadaQBO } = useCanadianTax();
  const [saved, setSaved] = useState(false);

  if ("SUPPLIER" !== draft.type) {
    throw new Error("Invalid vendor credit");
  }

  const handleSave = useCallback(
    async () => {
      try {
        await save();
        setSaved(true);
      } catch (e) {
        toast.error(
          isNew ?
            "Unable to create vendor credit" :
            "Unable to save vendor credit"
        );
      }
    },
    [isNew, save]
  );

  const handleClose = useCallback(
    () => onClose(saved),
    [onClose, saved]
  );

  const title = isNew
    ? "Create Vendor Credit"
    : `Vendor Credit ${vendorCredit.number}`;

  if (saved) {
    return (
      <FormPopup
        isNew={isNew}
        isEditable={false}
        isValid={isValid}
        onClose={handleClose}
        title={title}
        width={SUPPLIER_VENDOR_CREDIT_POPUP_WIDTH}
        height={SUPPLIER_VENDOR_CREDIT_POPUP_HEIGHT}
        customActions={<>
          <Button as="a" secondary href="/finance/vendor-credits">
            View Vendor Credits 
          </Button>
          <Button onClick={handleClose}>
            Close
          </Button>
        </>}
      >
        <AlertNotification alertType="success">
          {isNew ? 'Vendor credit created' : 'Vendor credit updated'}
        </AlertNotification>
        <span>You can find your vendor credits in the <b>Vendor Credits</b> tab of Billing</span>
      </FormPopup>
    );
  }

  return (
    <FormPopup
      title={title}
      onClose={handleClose}
      onSave={handleSave}
      isNew={isNew}
      isValid={isValid}
      isEditable={!disabled}
      width={SUPPLIER_VENDOR_CREDIT_POPUP_WIDTH}
      height={SUPPLIER_VENDOR_CREDIT_POPUP_HEIGHT}
    >
      <Row>
        <Col>
          <textarea
            style={{ minHeight: "4.5rem", margin: 0 }}
            value={draft.notes}
            onChange={(e) => updater.setNotes(e.target.value)}
            onBlur={(e) => updater.setNotes(draft.notes.trim())}
            placeholder={VENDOR_CREDIT_NOTE_PLACEHOLDER_TEXT}
            disabled={disabled}
          />
        </Col>
      </Row>
      <Row style={{ gap: "1rem" }}>
        <Col>
          <AsyncDivisionSelect
            label="Supplier"
            labelStyle={{ lineHeight: "24px" }}
            value={draft.division.id}
            onChange={(division) => updater.setDivision(division)}
            isDisabled={disabled}
          />
        </Col>
        {isCanadaQBO && (
          <Col>
            <ExemptTaxSelect
              value={draft.tax_id}
              label={"Tax Code"}
              labelStyle={{ lineHeight: "24px" }}
              onChange={(tax_id) => updater.setTaxId(tax_id)}
              isDisabled={disabled}
            />
          </Col>
        )}
        <Col>
          <LabeledIconInput
            label="Total amount to refund"
            labelOnTop
            Icon={<span>{getCurrencySymbol("en-US", draft.currency)}</span>}
            value={draft.total}
            onChange={(e) => updater.setTotal(e.target.value)}
            onBlur={(e) => updater.setTotal(Number.isNaN(Number(draft.total)) ? '0.00' : Number(draft.total).toFixed(2))}
            onFocus={(e) => e.target.select()}
          />
        </Col>
      </Row>
    </FormPopup>
  );
}
